export const BACKGROUND_TYPE_COLOR = 'color';
export const BACKGROUND_TYPE_GRADIENT = 'gradient';
export const BACKGROUND_TYPE_BLUR = 'blur';

export const OVERLAY_TYPE_SOLID = 'solid';
export const OVERLAY_TYPE_BLUR = 'blur';

export const DEFAULT_OVERLAY_COLOR = '#00000029';
export const DEFAULT_BLUR_BACKGROUND_COLOR = '#FFFFFF99';
export const DEFAULT_BLUR_INTENSITY = 30;
export const MAX_BLUR_PX = 40;

export const intensityToBlurPx = (intensity) => {
  const value = Number(intensity);
  if (Number.isFinite(value) === false) {
    return Math.round((DEFAULT_BLUR_INTENSITY / 100) * MAX_BLUR_PX);
  }
  const clamped = Math.min(100, Math.max(0, value));
  return Math.round((clamped / 100) * MAX_BLUR_PX);
};

// Small builder swatches are 32px — scale runtime blur down so intensity stays readable.
export const intensityToPreviewBlurPx = (intensity) =>
  Math.round(intensityToBlurPx(intensity) / 5);

const clampIntensity = (intensity) => {
  const value = Number(intensity);
  if (Number.isFinite(value) === false) {
    return DEFAULT_BLUR_INTENSITY;
  }
  return Math.min(100, Math.max(0, value));
};

const isUsableColor = (color) =>
  typeof color === 'string' && color.trim().length > 0;

// Overlay mode also sets CSS vars for undercity ::before layers (avoids host
// opacity animations blocking backdrop sampling). Direct filter remains as a
// fallback for builder previews that don't use those classes.
export const buildBlurCss = (color, intensity, {asOverlay = false} = {}) => {
  const blurPx = intensityToBlurPx(intensity);
  const filter = `blur(${blurPx}px) saturate(180%)`;

  if (asOverlay === true) {
    return {
      backgroundColor: color,
      backdropFilter: filter,
      WebkitBackdropFilter: filter,
      '--jimo-backdrop-color': color,
      '--jimo-backdrop-blur': `${blurPx}px`,
    };
  }

  return {
    backgroundColor: color,
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
  };
};

export const normalizeOverlay = (overlay) => {
  if (overlay == null) {
    return null;
  }
  if (typeof overlay === 'string') {
    if (isUsableColor(overlay) === false) {
      return null;
    }
    return {
      type: OVERLAY_TYPE_SOLID,
      color: overlay,
    };
  }
  if (typeof overlay !== 'object') {
    return null;
  }
  const color = overlay.color ?? null;
  if (isUsableColor(color) === false) {
    return null;
  }
  const type =
    overlay.type === OVERLAY_TYPE_BLUR ? OVERLAY_TYPE_BLUR : OVERLAY_TYPE_SOLID;
  return {
    type,
    color,
    ...(type === OVERLAY_TYPE_BLUR
      ? {intensity: clampIntensity(overlay.intensity)}
      : {}),
  };
};

export const resolveOverlayCss = (overlay) => {
  const normalized = normalizeOverlay(overlay);
  if (normalized == null) {
    return null;
  }
  if (normalized.type === OVERLAY_TYPE_BLUR) {
    return buildBlurCss(normalized.color, normalized.intensity, {
      asOverlay: true,
    });
  }
  return {
    backgroundColor: normalized.color,
  };
};

export const getOverlayColor = (overlay) => {
  const normalized = normalizeOverlay(overlay);
  return normalized?.color ?? null;
};

export const isOverlayBlur = (overlay) => {
  const normalized = normalizeOverlay(overlay);
  return normalized?.type === OVERLAY_TYPE_BLUR;
};

const resolveBackgroundType = (type) => {
  if (type === BACKGROUND_TYPE_GRADIENT) {
    return BACKGROUND_TYPE_GRADIENT;
  }
  if (type === BACKGROUND_TYPE_BLUR) {
    return BACKGROUND_TYPE_BLUR;
  }
  // Changelog uses "solid"; treat as color for shared CSS resolution
  return BACKGROUND_TYPE_COLOR;
};

export const normalizeBackground = (styleOrBackground) => {
  if (styleOrBackground == null || typeof styleOrBackground !== 'object') {
    return null;
  }

  const looksLikeBackgroundObject =
    styleOrBackground.type != null ||
    styleOrBackground.primaryColor != null ||
    styleOrBackground.secondaryColor != null ||
    styleOrBackground.animated != null;

  let background = null;
  let backgroundColor = null;

  if (
    styleOrBackground.background != null &&
    typeof styleOrBackground.background === 'object'
  ) {
    background = styleOrBackground.background;
    backgroundColor = styleOrBackground.backgroundColor ?? null;
  } else if (looksLikeBackgroundObject === true) {
    background = styleOrBackground;
  } else if (
    Object.prototype.hasOwnProperty.call(styleOrBackground, 'backgroundColor')
  ) {
    backgroundColor = styleOrBackground.backgroundColor;
  } else {
    return null;
  }

  if (background != null && typeof background === 'object') {
    const type = resolveBackgroundType(background.type);
    const primaryColor =
      background.primaryColor ?? backgroundColor ?? background.color ?? null;
    return {
      type,
      animated: background.animated === true,
      primaryColor: isUsableColor(primaryColor) ? primaryColor : null,
      secondaryColor: isUsableColor(background.secondaryColor)
        ? background.secondaryColor
        : null,
      ...(type === BACKGROUND_TYPE_BLUR
        ? {intensity: clampIntensity(background.intensity)}
        : {}),
      ...(background.shape != null ? {shape: background.shape} : {}),
      ...(background.shapeType != null
        ? {shapeType: background.shapeType}
        : {}),
    };
  }

  if (isUsableColor(backgroundColor) === true) {
    return {
      type: BACKGROUND_TYPE_COLOR,
      animated: false,
      primaryColor: backgroundColor,
      secondaryColor: null,
    };
  }

  return null;
};

export const toBackgroundStyleUpdate = (background) => {
  const normalized = normalizeBackground(background);
  if (normalized == null) {
    return {
      background: null,
      backgroundColor: null,
    };
  }
  return {
    background: normalized,
    backgroundColor: normalized.primaryColor,
  };
};

export const resolveBackgroundCss = (styleOrBackground) => {
  const background = normalizeBackground(styleOrBackground);
  if (background == null || background.primaryColor == null) {
    return {};
  }

  if (background.type === BACKGROUND_TYPE_BLUR) {
    return buildBlurCss(background.primaryColor, background.intensity);
  }

  if (background.type === BACKGROUND_TYPE_COLOR) {
    return {
      backgroundColor: background.primaryColor,
    };
  }

  if (background.type === BACKGROUND_TYPE_GRADIENT) {
    const {animated, primaryColor, secondaryColor} = background;
    if (animated === true) {
      return {
        backgroundColor: secondaryColor || '#fff',
      };
    }
    const primary =
      primaryColor?.length > 7
        ? `${primaryColor.slice(0, 7)}80`
        : `${primaryColor}80`;
    return {
      backgroundImage: `linear-gradient(180deg, ${primaryColor}, ${primary})`,
      backgroundColor: secondaryColor,
    };
  }

  return {};
};
